// Operation checklists (docs/op-checklists-plan.md), shared by the techs app, the office WO
// panel and the checklist builder. Pure (no Firebase), so tests/unit/checklists.test.mjs runs it.
//
// Where a list comes from, for an op with an OpCode:
//   1. this boat's own list for that op code (/checklists/boats/{boatId}/{code}), else
//   2. the best saved template for the code (/checklists/templates, right season first).
// It is applied automatically the first time the op is opened, and copied onto the op
// (/data/{opId}/Checklist), so later edits to a template or the boat's list never change it.
// A wrong list is switched or cleared from the op; clearing leaves Source.kind 'none', so it
// isn't applied again.

export const KINDS = ['check', 'reading'];
export const SCOPES = ['engine', 'boat'];
export const UNITS = ['h', 'V', 'psi', 'rpm', '°F', 'gal', 'in', 'ft', 'lbs'];
export const FUEL_TYPES = ['Gasoline', 'Diesel', 'Electric', 'Propane'];
export const SEASONS = ['spring', 'fall', 'any'];
export const BOAT_KEY = 'boat';
export const BOAT_FIELDS = ['Fuel_Capacity', 'Boat_Weight', 'Boat_Draft', 'Mast_Length', 'Boat_Length', 'Boat_Beam'];

const str = v => (v == null ? '' : String(v)).trim();
export const normCode = c => str(c).toUpperCase();

export function newItemId(now = Date.now(), rnd = Math.random) {
  return `i${now.toString(36)}${rnd().toString(36).slice(2, 6)}`;
}

/** One item as it's stored (template, boat list or op). Throws on a missing name. */
export function cleanItem(raw = {}, order = 0) {
  const Name = str(raw.Name).slice(0, 120);
  if (!Name) throw new Error('An item needs a name');
  const Kind = raw.Kind === 'reading' ? 'reading' : 'check';
  const item = { Name, Kind, Scope: raw.Scope === 'boat' ? 'boat' : 'engine', Order: Number.isFinite(+raw.Order) ? +raw.Order : order };
  if (str(raw.Name_ES)) item.Name_ES = str(raw.Name_ES).slice(0, 120);
  if (Kind === 'reading') item.Unit = str(raw.Unit).slice(0, 8);
  if (raw.Stabilizer) item.Stabilizer = true;
  if (raw.Engine_Hours && Kind === 'reading' && item.Scope === 'engine') item.Engine_Hours = true;
  // A boat-wide reading can fill a boat fact (boatDataCore CHECKLIST_FIELDS), e.g. the fuel tank size.
  if (BOAT_FIELDS.includes(raw.Boat_Field) && Kind === 'reading' && item.Scope === 'boat') item.Boat_Field = raw.Boat_Field;
  return item;
}

export const itemName = (item, lang) => (lang === 'ES' && item.Name_ES) || item.Name || '';

/** 'spring' or 'fall' for an op: its category when it says, else the month it's scheduled (or today). */
export function seasonOf(op = {}, today = new Date()) {
  const cat = str(op.Category).toLowerCase();
  if (/winter/.test(cat)) return 'fall';
  if (/spring/.test(cat)) return 'spring';
  const m = /^(\d{4})-(\d{2})/.exec(str(op.Schedule_Date));
  const month = m ? +m[2] : today.getMonth() + 1;
  return month <= 7 ? 'spring' : 'fall';
}

/** The boat's engines that get checklist tabs (same filter as pmpShared boatEngines). */
export function boatEngines(boat) {
  return Object.entries(boat?.Engines || {})
    .filter(([, e]) => e && typeof e === 'object' && !e.Retired && (e.Engine_Make || e.Engine_Model || e.Engine_Type))
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([key, e]) => ({ key, engine: e, type: str(e.Engine_Type) }));
}

export function boatEngineTypes(boat) {
  const types = new Set(boatEngines(boat).map(e => e.type).filter(Boolean));
  if (!types.size && str(boat?.Engine_Type)) types.add(str(boat.Engine_Type));
  return types;
}

/** Templates for an op code, best first: right season, then engine type, then most recently saved. */
export function rankTemplates(code, templates = {}, { season, engineTypes = new Set() } = {}) {
  const c = normCode(code);
  if (!c) return [];
  return Object.entries(templates || {})
    .filter(([, t]) => t && t.OpCodes && Object.keys(t.OpCodes).some(k => normCode(k) === c) && Object.keys(t.Items || {}).length)
    .map(([id, t]) => {
      const s = t.Season || 'any';
      const seasonOk = s === 'any' || !season || s === season;
      const engineOk = !t.Engine_Type || engineTypes.has(t.Engine_Type);
      return { id, t, seasonOk, engineOk, score: (seasonOk ? (s === 'any' ? 2 : 4) : 0) + (engineOk ? (t.Engine_Type ? 2 : 1) : 0) };
    })
    .sort((a, b) => b.score - a.score || (b.t.Updated_At || 0) - (a.t.Updated_At || 0) || a.id.localeCompare(b.id));
}

const countItems = items => Object.values(items || {}).filter(Boolean).length;

/**
 * Which list an op should get, and what else could be picked instead.
 * boatLists: this boat's /checklists/boats/{boatId} ({ code: { Items, ... } }).
 * Returns { auto: { kind, id, name, engineType, items } | null, options: [{ kind, id, name, count, seasonOk }] }.
 */
export function chooseList({ op = {}, boat = null, boatLists = {}, templates = {}, today = new Date() } = {}) {
  const code = normCode(op.OpCode);
  if (!code) return { auto: null, options: [] };
  const own = boatLists?.[code];
  const ranked = rankTemplates(code, templates, { season: seasonOf(op, today), engineTypes: boatEngineTypes(boat) });
  const options = [];
  if (own && countItems(own.Items)) {
    options.push({ kind: 'boat', id: code, name: own.Name || '', count: countItems(own.Items), seasonOk: true });
  }
  ranked.forEach(r => options.push({ kind: 'template', id: r.id, name: r.t.Name || '', count: countItems(r.t.Items), seasonOk: r.seasonOk }));
  const pick = options.find(o => o.kind === 'boat') || options.find(o => o.seasonOk) || null;
  return { auto: pick ? listFor(pick, { boatLists, templates, code }) : null, options };
}

/**
 * Saved lists that aren't for this op code, by name: any of them can still be put on an op
 * (and the office can add the op code to it). Same shape as chooseList()'s options, plus codes.
 */
export function otherTemplates(code, templates = {}) {
  const c = normCode(code);
  return Object.entries(templates || {})
    .filter(([, t]) => t && countItems(t.Items) && !Object.keys(t.OpCodes || {}).some(k => normCode(k) === c))
    .map(([id, t]) => ({ kind: 'template', id, name: t.Name || '', count: countItems(t.Items), seasonOk: true, codes: Object.keys(t.OpCodes || {}).map(normCode) }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** The op code as a saved list keeps it ('' when it can't be one, e.g. "WIN 4CYL"). */
export const templateCode = code => Object.keys(parseOpCodes(code)).find(k => k === normCode(code)) || '';

/** The items and name of one option from chooseList(). */
export function listFor(option, { boatLists = {}, templates = {}, code = '' } = {}) {
  const src = option.kind === 'boat' ? boatLists?.[normCode(code || option.id)] : templates?.[option.id];
  if (!src) return null;
  return { kind: option.kind, id: option.id, name: src.Name || option.name || '', engineType: src.Engine_Type || '', items: src.Items || {} };
}

/**
 * The op's own copy of a list: /data/{opId}/Checklist. `list` from listFor()/chooseList().auto,
 * or null for "no checklist" (cleared). Item ids are kept, so the boat's list and the op line up.
 */
export function snapshot(list, { by = '', at = Date.now(), boat = null } = {}) {
  if (!list) return { Source: { kind: 'none' }, Set_By: by, Set_At: at };
  const Items = {};
  Object.entries(list.items || {}).forEach(([iid, it], i) => {
    try { Items[iid] = cleanItem(it, i); } catch { /* a nameless item is skipped */ }
  });
  const out = { Source: { kind: list.kind, id: list.id, name: list.name || '' }, Items, Set_By: by, Set_At: at };
  if (list.engineType) out.Engine_Type = list.engineType;
  const engines = engineCopy(boat);
  if (engines) out.Engines = engines;
  return out;
}

/** The boat's engines as the op keeps them (enough for the tabs), so a list can be counted without the boat. */
export function engineCopy(boat) {
  const engines = boatEngines(boat);
  if (!engines.length) return null;
  const keep = ['Engine_Make', 'Engine_Model', 'Engine_Type', 'Engine_Position'];
  return Object.fromEntries(engines.map(e => [e.key, Object.fromEntries(keep.filter(k => str(e.engine[k])).map(k => [k, str(e.engine[k])]))]));
}

export const isCleared = cl => !!cl && cl.Source?.kind === 'none' && !liveItems(cl).length;

/** The op's items still on the list, in order. */
export function liveItems(cl) {
  return Object.entries(cl?.Items || {})
    .filter(([, it]) => it && it.Name && !it.Removed_At)
    .map(([iid, it]) => ({ iid, ...it }))
    .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0) || a.iid.localeCompare(b.iid));
}

export const removedItems = cl => Object.entries(cl?.Items || {})
  .filter(([, it]) => it && it.Removed_At)
  .map(([iid, it]) => ({ iid, ...it }));

/**
 * Tabs for the op screen: one per engine the list's engine items apply to, then Boat.
 * Engine items go on engines of the list's Engine_Type when it has one and the boat has such
 * engines; otherwise on every engine but a generator (or every engine, if that's all there is).
 * With no engines on file there's only the Boat tab, and every item shows there.
 */
export function engineTabs(boat, cl = null, lang = 'EN') {
  // The boat when it's at hand (it may have changed since), else the engines the op kept.
  let all = boatEngines(boat);
  if (!all.length && cl?.Engines) all = boatEngines({ Engines: cl.Engines });
  const want = str(cl?.Engine_Type);
  let engines = want ? all.filter(e => e.type === want) : [];
  if (!engines.length) engines = all.filter(e => e.type !== 'Generator');
  if (!engines.length) engines = all;
  // Port / Stbd when on file, else Engine 1, Engine 2 (twins are usually the same model, so the
  // model only tells them apart in the tooltip; "1 · F200" next to a 0/7 count read as a fraction).
  const word = lang === 'ES' ? 'Motor' : 'Engine';
  const tabs = engines.map((e, i) => ({
    key: e.key,
    label: engines.length === 1 ? word : str(e.engine.Engine_Position) || `${word} ${i + 1}`,
    title: [e.engine.Engine_Make, e.engine.Engine_Model].map(str).filter(Boolean).join(' '),
  }));
  tabs.push({ key: BOAT_KEY, label: lang === 'ES' ? 'Barco' : 'Boat', title: '' });
  return tabs;
}

/** Every box to tick: { iid, item, key } (key = engine key or 'boat'). */
export function cellsOf(cl, boat) {
  const engineKeys = engineTabs(boat, cl).map(t => t.key).filter(k => k !== BOAT_KEY);
  const cells = [];
  liveItems(cl).forEach(item => {
    const keys = item.Scope === 'engine' && engineKeys.length ? engineKeys : [BOAT_KEY];
    keys.forEach(key => cells.push({ iid: item.iid, item, key }));
  });
  return cells;
}

export function isDone(cl, iid, key) {
  const d = cl?.Done?.[iid]?.[key];
  if (!d) return false;
  return cl.Items?.[iid]?.Kind === 'reading' ? str(d.Value) !== '' : true;
}

/** Counts overall and per tab, plus the open boxes grouped by tab (for the Finish warning). */
export function progress(cl, boat, lang = 'EN') {
  const tabs = engineTabs(boat, cl, lang);
  const byTab = Object.fromEntries(tabs.map(t => [t.key, { done: 0, total: 0 }]));
  const open = [];
  let done = 0, total = 0;
  cellsOf(cl, boat).forEach(c => {
    const ok = isDone(cl, c.iid, c.key);
    total++; byTab[c.key].total++;
    if (ok) { done++; byTab[c.key].done++; } else open.push(c);
  });
  const openGroups = tabs
    .map(t => ({ key: t.key, label: t.label, items: open.filter(c => c.key === t.key).map(c => c.item) }))
    .filter(g => g.items.length);
  return { done, total, byTab, tabs, open: open.length, openGroups };
}

/** The meter on office op rows: none | progress | done | open-finished. */
export function meter(cl, boat, status) {
  const p = progress(cl, boat);
  if (!p.total) return { ...p, state: 'none' };
  if (p.done === p.total) return { ...p, state: 'done' };
  const finished = ['Ready to Bill', 'Complete', 'Closed'].includes(str(status));
  return { ...p, state: finished ? 'open-finished' : 'progress' };
}

/** The stabilizer line for a boat: { fuel, name, dose } (name '' when none is set for that fuel). */
export function stabilizerFor(boat, stabilizers = {}) {
  const fuel = str(boat?.Fuel_Type);
  if (!fuel) return { fuel: '', name: '', dose: '' };
  const s = stabilizers?.[fuel] || {};
  return { fuel, name: str(s.Name), dose: str(s.Dose) };
}

/**
 * What the boat keeps for next time, from the op's list: items added "just this time" (Once)
 * are left out, and items removed "just this time" (Removed_Once) stay in.
 */
export function boatListFromOp(cl) {
  const Items = {};
  Object.entries(cl?.Items || {}).forEach(([iid, it]) => {
    if (!it || !it.Name) return;
    if (it.Once) return;
    if (it.Removed_At && !it.Removed_Once) return;
    Items[iid] = cleanItem(it);
  });
  const out = { Name: cl?.Source?.name || '', Items };
  if (cl?.Engine_Type) out.Engine_Type = cl.Engine_Type;
  return out;
}

export const ENGINE_TYPES = ['Outboard', 'Inboard', 'Sterndrive', 'IPS', 'Jet', 'Generator'];

/** "6160, 6150 6174" → { '6160': true, '6150': true, '6174': true } */
export function parseOpCodes(text) {
  const codes = String(text || '').split(/[\s,;]+/).map(normCode).filter(c => /^[A-Z0-9][A-Z0-9_-]{0,19}$/.test(c));
  return Object.fromEntries([...new Set(codes)].map(c => [c, true]));
}

/**
 * A saved checklist as the office's builder saves it. `items` is the editor's list in order
 * ([{ iid, Name, ... }]); throws with what to fix.
 */
export function cleanTemplate({ name, season, engineType, opCodes, items } = {}) {
  const Name = str(name).slice(0, 80);
  if (!Name) throw new Error('Give the checklist a name');
  const OpCodes = typeof opCodes === 'string' ? parseOpCodes(opCodes) : (opCodes || {});
  if (!Object.keys(OpCodes).length) throw new Error('Add at least one op code it is for');
  const Items = {};
  (items || []).filter(it => str(it?.Name)).forEach((it, i) => { Items[it.iid] = cleanItem({ ...it, Order: i }); });
  if (!Object.keys(Items).length) throw new Error('Add at least one item');
  const out = { Name, Season: SEASONS.includes(season) ? season : 'any', OpCodes, Items };
  if (ENGINE_TYPES.includes(engineType)) out.Engine_Type = engineType;
  return out;
}

/** A device's copy of a record, after an update() of path keys ('Checklist/Done/a/b': v; null deletes). */
export function applyPaths(obj, updates) {
  Object.entries(updates || {}).forEach(([path, v]) => {
    const keys = path.split('/').filter(Boolean);
    let o = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (o[keys[i]] == null || typeof o[keys[i]] !== 'object') {
        if (v == null) return;
        o[keys[i]] = {};
      }
      o = o[keys[i]];
    }
    const last = keys[keys.length - 1];
    if (v == null) delete o[last]; else o[last] = JSON.parse(JSON.stringify(v));
  });
  return obj;
}

/** Next Order for an item added to the end of a list. */
export const nextOrder = items => Object.values(items || {}).reduce((m, it) => Math.max(m, Number(it?.Order) || 0), -1) + 1;
